import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAlert } from "react-alert";
import {
  Table, TableHead, TableBody, TableRow, TableCell, Chip, IconButton,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Alert
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import Menu from "../Menu";
import Header from "../Header";
import PreviewPane from "./builder/PreviewPane";
import { FormsApi, fmtDateTime } from "./formsUtils";
import { errorMessage } from "../../services/http";

const ERROR_TIMEOUT = 15000;

export default function FormsTable() {
  const alert = useAlert();
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [newForm, setNewForm] = useState({ title: "", description: "" });
  const [openImport, setOpenImport] = useState(false);
  const [importData, setImportData] = useState({ formId: "", apiKey: "", region: "us" });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [preview, setPreview] = useState({ open: false, loading: false, form: null });

  function load() {
    setLoading(true);
    FormsApi.list("?page=0&limit=100").then(
      (resp) => { setRows(resp?.data?.data || []); setLoading(false); },
      (error) => { console.log(error); setLoading(false); }
    );
  }

  useEffect(() => { load(); }, []);

  function handleCreate() {
    if (!newForm.title.trim()) {
      alert.error("Informe um título", { timeout: ERROR_TIMEOUT });
      return;
    }
    FormsApi.create(newForm).then(
      (resp) => {
        setOpenNew(false);
        setNewForm({ title: "", description: "" });
        history.push("/forms-builder", { formId: resp.data.id });
      },
      (error) => {
        alert.error(errorMessage(error, "Erro ao criar formulário"), { timeout: ERROR_TIMEOUT });
      }
    );
  }

  function handlePreview(row) {
    setPreview({ open: true, loading: true, form: null });
    FormsApi.get(row.id).then(
      (resp) => setPreview({ open: true, loading: false, form: resp.data }),
      (error) => {
        setPreview({ open: false, loading: false, form: null });
        alert.error(errorMessage(error, "Erro ao carregar o formulário"), { timeout: ERROR_TIMEOUT });
      }
    );
  }

  function handleImport() {
    if (!importData.formId.trim() || !importData.apiKey.trim()) {
      alert.error("Informe o form ID e a API key do JotForm", { timeout: ERROR_TIMEOUT });
      return;
    }
    setImporting(true);
    setImportResult(null);
    FormsApi.importJotform({
      formId: importData.formId.trim(),
      apiKey: importData.apiKey.trim(),
      region: importData.region,
    }).then(
      (resp) => {
        setImporting(false);
        const { id, fieldCount, logicCount, unmapped } = resp.data;
        alert.success(`Importado: ${fieldCount} campo(s)` + (logicCount ? `, ${logicCount} regra(s) de lógica` : "") + (unmapped?.length ? `, ${unmapped.length} não mapeado(s)` : ""));
        if (unmapped && unmapped.length) {
          setImportResult({ unmapped });
          // dá um tempo para o usuário ver o relatório antes de ir ao builder
          setTimeout(() => history.push("/forms-builder", { formId: id }), 400);
        } else {
          history.push("/forms-builder", { formId: id });
        }
      },
      (error) => {
        setImporting(false);
        alert.error(errorMessage(error, "Erro ao importar do JotForm"), { timeout: ERROR_TIMEOUT });
      }
    );
  }

  function handleDelete(row) {
    if (!window.confirm(`Excluir o formulário "${row.title}"?`)) return;
    FormsApi.remove(row.id).then(
      () => { alert.success("Removido!"); load(); },
      (error) => { alert.error(errorMessage(error, "Erro ao remover"), { timeout: ERROR_TIMEOUT }); }
    );
  }

  const statusChip = (status) => {
    const map = {
      draft: { label: "Rascunho", color: "default" },
      published: { label: "Publicado", color: "success" },
    };
    const cfg = map[status] || map.draft;
    return <Chip size="small" label={cfg.label} color={cfg.color} />;
  };

  return (
    <div id="wrapper">
      <Menu />
      <div id="page-wrapper" className="gray-bg">
        <Header />
        <div className="row wrapper border-bottom white-bg page-heading">
          <div className="col-lg-8">
            <h2>Formulários</h2>
            <small>Construtor de pesquisas e fluxos dinâmicos</small>
          </div>
          <div className="col-lg-4" style={{ textAlign: "right", paddingTop: 20 }}>
            <Button startIcon={<CloudDownloadIcon />} onClick={() => { setImportResult(null); setOpenImport(true); }} sx={{ mr: 1 }}>
              Importar do JotForm
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenNew(true)}>
              Novo formulário
            </Button>
          </div>
        </div>

        <div className="wrapper wrapper-content animated fadeInRight">
          <div className="row">
            <div className="col-lg-12">
              <div className="ibox">
                <div className="ibox-content">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Título</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Respostas</TableCell>
                        <TableCell>Atualizado</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <strong>{row.title}</strong>
                            {row.description ? <div style={{ color: "#888", fontSize: 12 }}>{row.description}</div> : null}
                          </TableCell>
                          <TableCell>{statusChip(row.status)}</TableCell>
                          <TableCell align="center">{row.responses}</TableCell>
                          <TableCell>{fmtDateTime(row.updated_at)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Visualizar">
                              <IconButton size="small" onClick={() => handlePreview(row)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar / Construir">
                              <IconButton size="small" onClick={() => history.push("/forms-builder", { formId: row.id })}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Respostas">
                              <IconButton size="small" onClick={() => history.push("/forms-responses", { formId: row.id, title: row.title })}>
                                <AssessmentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!loading && rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" style={{ color: "#999", padding: 40 }}>
                            Nenhum formulário criado ainda. Clique em "Novo formulário".
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={openNew} onClose={() => setOpenNew(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo formulário</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Título *" fullWidth variant="outlined"
            value={newForm.title}
            onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
          />
          <TextField
            margin="dense" label="Descrição" fullWidth multiline rows={3} variant="outlined"
            value={newForm.description}
            onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}>Criar e construir</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openImport} onClose={() => setOpenImport(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar do JotForm</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Importa os campos, opções e obrigatoriedade. Campos sem equivalente (widgets, assinatura, etc.)
            viram um aviso no formulário e entram no relatório. Lógica e escore você ajusta depois no builder.
          </Alert>
          <TextField autoFocus margin="dense" label="Form ID do JotForm *" fullWidth variant="outlined"
            value={importData.formId}
            onChange={(e) => setImportData({ ...importData, formId: e.target.value })} />
          <TextField margin="dense" label="API Key *" fullWidth variant="outlined" type="password"
            value={importData.apiKey}
            onChange={(e) => setImportData({ ...importData, apiKey: e.target.value })} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Região da conta</InputLabel>
            <Select label="Região da conta" value={importData.region}
              onChange={(e) => setImportData({ ...importData, region: e.target.value })}>
              <MenuItem value="us">EUA (api.jotform.com)</MenuItem>
              <MenuItem value="eu">Europa (eu-api.jotform.com)</MenuItem>
            </Select>
          </FormControl>

          {importResult && importResult.unmapped && importResult.unmapped.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {importResult.unmapped.length} campo(s) não mapeado(s):
              <ul style={{ margin: "6px 0 0 0", paddingLeft: 18 }}>
                {importResult.unmapped.slice(0, 8).map((u, i) => (
                  <li key={i}>{u.text || u.qid} <em>({u.type})</em></li>
                ))}
              </ul>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImport(false)}>Fechar</Button>
          <Button variant="contained" onClick={handleImport} disabled={importing}>
            {importing ? "Importando..." : "Importar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={preview.open} onClose={() => setPreview({ open: false, loading: false, form: null })}
        maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Visualizar: {preview.form?.title || ""}</span>
          <IconButton size="small" onClick={() => setPreview({ open: false, loading: false, form: null })}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {preview.loading || !preview.form ? (
            <div style={{ textAlign: "center", padding: 40 }}><CircularProgress /></div>
          ) : (
            <PreviewPane
              definition={preview.form.version?.definition || { pages: [], fields: [] }}
              title={preview.form.title}
              description={preview.form.description}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
